// Navigation Handler for PracticeTest
// Extracted to reduce file size and prevent Cursor crashes

import { lightHaptic } from '../../utils/haptics';
import { questionTracker } from '../../services/questionTracker';
import { testCountTracker } from '../../services/testCountTracker';
import { aiCoach } from '../../services/aiCoach';
import { deferredWeakAreaQueue } from '../../services/deferredWeakAreaQueue';
import { conversationMemory } from '../../services/conversationMemory';
import {
  getAllTopicPerformance,
  updateTopicPerformance,
  incrementCompletedTestCount,
} from '../../services/topicPerformanceService';
import { generateAndStoreAdaptiveExam } from '../../services/adaptiveExamService';
import { userAuth } from '../../services/userAuth';
import { logger } from '../../utils/logger';
import { safeArrayAt } from '../../utils/safeRender';
import type { HandlersContext } from './PracticeTest.handlers.types';
import { resetAllCoaching } from './PracticeTest.handlers.coaching';

// Next question handler
export const createNextQuestion = (context: HandlersContext) => {
  return () => {
    const {
      state,
      dispatch,
      questions,
      testId,
      previousAnswerCorrectRef,
      getTestName,
    } = context;

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        if (!state.isMuted) lightHaptic();
      });
    } else {
      requestAnimationFrame(() => {
        if (!state.isMuted) lightHaptic();
      });
    }
    
    // Clear conversation memory for this question (no memory across questions)
    // This ensures AI doesn't remember previous questions
    conversationMemory.clearHistory();
    
    // CRITICAL: Clear all coaching state before moving to next question
    // This ensures follow-ups from previous question don't leak into the new one
    resetAllCoaching();
    
    if (state.currentQuestionIndex < questions.length - 1) {
      // Single dispatch for all question state changes (atomic update)
      // Store previous answer correctness for transition message (safe index – Rule 1)
      const previousQ = safeArrayAt(questions, state.currentQuestionIndex);
      if (previousQ && state.selectedAnswer) {
        previousAnswerCorrectRef.current = state.selectedAnswer === previousQ.correctAnswerId;
      }
      
      dispatch({ 
        type: 'NEXT_QUESTION', 
        payload: { questionStartTime: Date.now() } 
      });

      // Chat history is rebuilt per question (strict isolation)
    } else {
      // Test complete - show results INSTANTLY (no delays, no async operations)
      const percentage = Math.round((state.score / questions.length) * 100);
      const pctForPractice = percentage; // alias for closure use in requestIdleCallback below

      // Update counts before COMPLETE_TEST so results (showAiExamCTA) read correct localStorage on first paint
      if (testId && testId !== 'mock-exam' && !testId.includes('mock')) {
        try {
          testCountTracker.incrementTestCount();
          testCountTracker.addUniqueTopicCompleted(testId);
          const newCount = testCountTracker.getTestCount();
          if (newCount === 3) {
            testCountTracker.setFreeAiExamPending(true);
          }
        } catch (error) {
          logger.error('Failed to increment test count:', error);
        }
      }

      dispatch({ type: 'COMPLETE_TEST' });

      if (testId !== 'mock-exam') {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem('practiceTestFirstEverCompleted', 'true');
        }
      }
      
      // All async operations happen AFTER UI update (non-blocking)
      // Use requestIdleCallback for maximum performance
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(() => {
          // Track question IDs to prevent repetition (Netflix-style)
          if (testId && testId !== 'mock-exam' && !testId.includes('mock')) {
            const questionIds = questions.map(q => q.id);
            questionTracker.trackQuestions(testId, questionIds);
          }

          // Save result asynchronously. Practice: read from session only. Exam: use state (no shared selectors).
          const scoreForSave = testId !== 'mock-exam' && state.session
            ? state.session.answers.filter(a => a.isCorrect).length
            : state.score;
          const totalForSave = testId !== 'mock-exam' && state.session
            ? state.session.sessionQuestions.length
            : questions.length;
          const dateForSave = testId !== 'mock-exam' && state.session?.completedAt
            ? new Date(state.session.completedAt).toISOString()
            : new Date().toISOString();
          const percentageForSave = totalForSave > 0 ? Math.round((scoreForSave / totalForSave) * 100) : pctForPractice;
          aiCoach.saveTestResult({
            testId: testId || '',
            testName: getTestName(),
            score: scoreForSave,
            totalQuestions: totalForSave,
            percentage: percentageForSave,
            date: dateForSave
          }).catch(err => {
            if ('requestIdleCallback' in window) {
              (window as any).requestIdleCallback(() => {
                logger.error('Failed to save test result:', err);
              });
            }
          });

          // Add weak area to deferred queue if score < 60%
          if (pctForPractice < 60 && testId && testId !== 'mock-exam' && !testId.includes('mock')) {
            deferredWeakAreaQueue.addWeakArea(testId, pctForPractice);
            logger.debug('Added weak area to deferred queue:', { testId, percentage: pctForPractice });
          }

          // Weak-topics drill loop: require 2 consecutive >=70% to mark "mastered" (device-level)
          if (testId && testId !== 'mock-exam' && !testId.includes('mock')) {
            try {
              const streakKey = `weakTopicMasteryStreak:${testId}`;
              const masteredKey = `weakTopicMastered:${testId}`;
              const prevStreakRaw = localStorage.getItem(streakKey);
              const prevStreak = prevStreakRaw ? parseInt(prevStreakRaw, 10) : 0;
              const nextStreak = pctForPractice >= 70 ? prevStreak + 1 : 0;
              localStorage.setItem(streakKey, String(nextStreak));
              if (nextStreak >= 2) {
                localStorage.setItem(masteredKey, 'true');
              }
            } catch {
              /* ignore storage errors */
            }
          }

          // Check for mastery celebration (weak area crossed 60% threshold)
          if (pctForPractice >= 60 && testId && testId !== 'mock-exam' && !testId.includes('mock')) {
            const weakArea = deferredWeakAreaQueue.getWeakArea(testId);
            if (weakArea && weakArea.score < 60) {
              const previousScore = weakArea.score;
              const wasMastered = deferredWeakAreaQueue.updateWeakAreaAfterPractice(testId, pctForPractice);
              if (wasMastered) {
                logger.debug('Weak area mastered:', { testId, previousScore, currentScore: pctForPractice });
              }
            }
          }

          // Supabase: user_topic_performance + unique topic completion count (silent, non-blocking)
          const userId = localStorage.getItem('userId') || userAuth.getCurrentUser()?.id || null;
          if (userId && testId && testId !== 'mock-exam' && !testId.includes('mock')) {
            const correct = scoreForSave;
            const total = totalForSave;
            (async () => {
              try {
                const existingRows = await getAllTopicPerformance(userId);
                const topicExisted = existingRows.some((r) => r.topic_id === testId);
                await updateTopicPerformance(userId, {
                  topicId: testId,
                  totalQuestions: total,
                  correctAnswers: correct,
                });
                if (!topicExisted) {
                  const newCount = await incrementCompletedTestCount(userId);
                  if (newCount === 5) {
                    generateAndStoreAdaptiveExam(userId, 5).catch(err => {
                      logger.error('generateAndStoreAdaptiveExam error:', err);
                    });
                    if ('requestIdleCallback' in window) {
                      (window as any).requestIdleCallback(() => {
                        window.dispatchEvent(
                          new CustomEvent('aiExamUnlocked', {
                            detail: { message: 'AI Exam unlocked! You have 1 AI Personalised Exam available as a preview.' },
                          })
                        );
                      });
                    } else {
                      setTimeout(() => {
                        window.dispatchEvent(
                          new CustomEvent('aiExamUnlocked', {
                            detail: { message: 'AI Exam unlocked! You have 1 AI Personalised Exam available as a preview.' },
                          })
                        );
                      }, 100);
                    }
                  }
                }
              } catch (err) {
                logger.error('Supabase topic performance / completion count error:', err);
              }
            })();
          }
        });
      }
    }
  };
};
